/**
 * Réplica del contrato {@code HashSupport} / {@code Hashable} del backend
 * para comparar impacto financiero de suscripciones en el frontend.
 */

const NONE = 'None';
const CENTS = 100;
const MILLIS = 1000;

function token(value: unknown): string {
  if (value == null) return NONE;
  if (typeof value === 'string') return value.trim() === '' ? NONE : value;
  if (Array.isArray(value)) {
    if (value.length === 0) return NONE;
    return value
      .filter((v) => v != null && String(v).trim() !== '')
      .map(String)
      .join(',');
  }
  return String(value);
}

function join(...parts: unknown[]): string {
  return parts.map(token).join('|');
}

function normalizeId(id: number | null | undefined): number | null {
  return id == null || id === 0 ? null : id;
}

function toMillis(quantity: number | null | undefined): number | null {
  if (quantity == null) return null;
  const millis = Math.round(quantity * MILLIS);
  return millis === 0 ? null : millis;
}

function toCents(amount: number | null | undefined): number | null {
  if (amount == null) return null;
  const cents = Math.round(amount * CENTS);
  return cents === 0 ? null : cents;
}

async function sha256(payload: string): Promise<string> {
  const data = new TextEncoder().encode(payload);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export interface BillingLineHashInput {
  productId?: number | null;
  quantity?: number | null;
  unitPrice?: number | null;
  taxes?: { taxCode?: string | null }[] | null;
}

export function lineHashPayload(line: BillingLineHashInput): string {
  let taxKeys: string[] | null = null;
  if (line.taxes?.length) {
    taxKeys = line.taxes
      .filter((t) => t?.taxCode != null && String(t.taxCode).trim() !== '')
      .map((t) => String(t.taxCode).trim())
      .sort();
    if (taxKeys.length === 0) taxKeys = null;
  }
  return join(
    normalizeId(line.productId),
    toMillis(line.quantity),
    taxKeys,
    toCents(line.unitPrice)
  );
}

export async function lineSha256Hash(line: BillingLineHashInput): Promise<string> {
  return sha256(lineHashPayload(line));
}

export interface SubscriptionHashInput {
  currencyId?: number | null;
  subtotal?: number | null;
  tax?: number | null;
  discount?: number | null;
  total?: number | null;
  lineHashes: string[];
}

export function subscriptionHashPayload(input: SubscriptionHashInput): string {
  return join(
    normalizeId(input.currencyId),
    toCents(input.subtotal),
    toCents(input.tax),
    toCents(input.discount),
    toCents(input.total),
    input.lineHashes.length === 0 ? null : input.lineHashes.join(',')
  );
}

export async function subscriptionSha256Hash(input: SubscriptionHashInput): Promise<string> {
  return sha256(subscriptionHashPayload(input));
}
